import { upgradeTargetName } from "@highmarch/app";
import type { BalanceData, PlayerProfile } from "@highmarch/domain";
import { NO_HUB_EXTRAS, guide, type BusyReason, type HubExtras, type HubFacts } from "../guide/guide";
import { HUB_SHEET_TARGETS } from "../guide/scripts/hub";
import { HUB_SHEET_TIPS } from "../guide/scripts/features";
import type { BuilderPick } from "./builderPick";
import type { Readiness } from "./readiness";

/**
 * The hub as Captain Bram sees it (guide.ts HubFacts) and when he must wait (BusyReason): hubView.ts calls these on
 * every render and 1 s tick. Kept apart from the view so the rules are testable in node.
 */

/** A hub sheet's name for HubFacts.sheet, from its class list (the sheets' own classes, first match wins). */
const SHEET_NAMES: readonly (readonly [string, string])[] = [
  ["inventory", "gear"], ["upgrade", "upgrade"], ["towers", "towers"], ["chests", "chests"], ["guide", "guide"],
  ["checkin", "checkin"], ["commander", "commander"], ["settings", "settings"], ["shophub", "shop"], ["evhub", "evhub"],
  ["menu", "menu"], ["more", "more"], ["debug", "debug"],
];

export function sheetName(classes: readonly string[]): string | null {
  if (!classes.includes("sheet")) return null;
  return SHEET_NAMES.find(([cls]) => classes.includes(cls))?.[1] ?? "other";
}

export interface HubFactsInput {
  readonly B: BalanceData;
  readonly profile: PlayerProfile;
  readonly highestCleared: number;
  readonly nextLevel: number;
  readonly readiness: Readiness;
  readonly pick: BuilderPick;
  readonly newTowerIds: readonly string[];
  readonly ordersUnlocked: boolean;
  readonly sheet: string | null;
}

/**
 * The features' facts (HubFacts.extras), from features/guide.ts: set once at boot, read on every hub render. The hub
 * view stays frozen (FINISH_PLAN §3); a failing source never breaks the hub, it just teaches nothing new.
 */
let extrasSource: (() => HubExtras) | null = null;
export function setHubExtras(source: (() => HubExtras) | null): void { extrasSource = source; }
function hubExtras(): HubExtras {
  try { return extrasSource?.() ?? NO_HUB_EXTRAS; } catch { return NO_HUB_EXTRAS; }
}

export function hubFacts(i: HubFactsInput): HubFacts {
  const p = i.profile, r = i.readiness;
  const opt = i.pick.kind === "idle" && i.pick.actionable ? i.pick.option : null;
  return {
    highestCleared: i.highestCleared,
    nextLevel: i.nextLevel,
    equipped: p.gear.some((g) => g.equippedTo !== null),
    upgraded: p.jobs.length > 0 || p.baseLevel > 1 || p.towerLevels.some((l) => l > 1),
    builderSuggestion: opt ? { name: upgradeTargetName(i.B, opt.target, opt.towerIndex), seconds: p.durationFor(opt.level + 1) } : null,
    // Gear counts from here and the gear meter is short of the level's target (readiness: under-geared on gear).
    gearRequired: r.gearRequired && (r.shortfall === "gear" || r.shortfall === "both"),
    gearRequiredFrom: i.B.readiness.gearRequiredFrom,
    stashCount: p.gear.filter((g) => g.equippedTo === null).length,
    newTowerIds: i.newTowerIds,
    ordersUnlocked: i.ordersUnlocked,
    sheet: i.sheet,
    extras: hubExtras(),
  };
}

/** What the hub has on screen besides the lobby, as far as the guide's busy rule cares. */
export interface HubScreenState {
  readonly wipe: boolean;
  readonly unlockCards: boolean;
  /** The chest loot is still flying into the pills. */
  readonly lootFlying: boolean;
  /** A scrim dialog is up (a confirm, the away card, the install card, the backup box). */
  readonly dialog: boolean;
  /** The sheet on top: its name (sheetName) and whether it holds a guide target (HUB_SHEET_TARGETS). */
  readonly sheet: { readonly name: string; readonly holdsTarget: boolean } | null;
}

/**
 * Why the guide must wait (DESIGN §4.3): the cloud wipe, unlock cards, the loot flight, a dialog, or a sheet other
 * than one a step points into (the Armoury's Equip best, the upgrade sheet's UPGRADE). The check-in card is a dialog.
 */
export function hubBusy(s: HubScreenState): BusyReason | null {
  if (s.wipe) return "wipe";
  if (s.unlockCards) return "unlockCards";
  if (s.lootFlying) return "loading";
  if (s.dialog || s.sheet?.name === "checkin") return "dialog";
  if (s.sheet && !s.sheet.holdsTarget) return "sheet";
  return null;
}

/**
 * Whether a sheet holds something a hub step points at: a first-session target, or the target of a feature tip not
 * yet shown (QUEUE NEXT, a crate, a skin): once the tip is done the sheet is busy again, so nothing else speaks over it.
 */
export function holdsGuideTarget(sheet: ParentNode, isDone: (id: string) => boolean = (id) => guide()?.isDone(id) ?? true): boolean {
  return HUB_SHEET_TARGETS.some((sel) => sheet.querySelector(sel) !== null)
    || HUB_SHEET_TIPS.some((t) => !isDone(t.step) && sheet.querySelector(t.target) !== null);
}
